import { useMemo } from 'react';

import { usePreferencesStore } from '#lib/stores/preferencesStore';

const EMPTY_LAYOUT = { order: [], hidden: [], shown: [] };

/**
 * Picker label of a column; columns without one (actions, icons) are never offered.
 * @param {Object} column
 * @returns {string|null}
 */
export const layoutLabelOf = (column) =>
	column.layoutLabel ?? (typeof column.label === 'string' && column.label ? column.label : null);

/**
 * Saved order with columns the user has never seen inserted at their default position.
 * @param {string[]} savedOrder
 * @param {string[]} defaultKeys
 * @returns {string[]}
 */
function mergeOrder(savedOrder, defaultKeys) {
	const known = savedOrder.filter((key) => defaultKeys.includes(key));
	defaultKeys.forEach((key, index) => {
		if (!known.includes(key)) known.splice(Math.min(index, known.length), 0, key);
	});
	return known;
}

/**
 * User-controlled order and visibility of a table's columns, persisted per `layoutKey`. The
 * first column (row identity, when sticky), right-pinned columns, columns without a label and
 * columns with `hideable: false` keep their place; the rest can be hidden and reordered.
 * Columns with `defaultHidden` stay hidden until the user shows them.
 *
 * @param {string|undefined} layoutKey - No key = the columns as given, no picker
 * @param {Object[]} columns
 * @param {boolean} firstColumnSticky
 * @returns {{columns: Object[], picker: Object|null}}
 */
export function useTableLayout(layoutKey, columns, firstColumnSticky) {
	const saved = usePreferencesStore((s) => (layoutKey ? s.tableLayouts?.[layoutKey] : undefined));
	const setTableLayout = usePreferencesStore((s) => s.setTableLayout);

	return useMemo(() => {
		if (!layoutKey) return { columns, picker: null };

		const layout = { ...EMPTY_LAYOUT, ...saved };
		const isMovable = (column, index) =>
			column.hideable !== false &&
			!(firstColumnSticky && index === 0) &&
			column.sticky !== 'right' &&
			Boolean(layoutLabelOf(column));
		const movable = columns.filter(isMovable);
		const byKey = new Map(movable.map((column) => [column.key, column]));
		const order = mergeOrder(
			layout.order,
			movable.map((column) => column.key),
		);
		const isVisible = (column) =>
			column.defaultHidden ? layout.shown.includes(column.key) : !layout.hidden.includes(column.key);

		const orderedMovable = order.map((key) => byKey.get(key));
		let slot = 0;
		const arranged = columns.map((column, index) => (isMovable(column, index) ? orderedMovable[slot++] : column));
		const visibleColumns = arranged.filter((column) => !byKey.has(column.key) || isVisible(column));

		const save = (patch) => setTableLayout(layoutKey, { ...layout, order, ...patch });
		const toggle = (key) => {
			const column = byKey.get(key);
			const without = (list) => list.filter((k) => k !== key);
			save(
				isVisible(column)
					? { hidden: [...without(layout.hidden), key], shown: without(layout.shown) }
					: { shown: [...without(layout.shown), key], hidden: without(layout.hidden) },
			);
		};
		const move = (fromKey, toKey) => {
			if (fromKey === toKey) return;
			const next = order.filter((key) => key !== fromKey);
			next.splice(next.indexOf(toKey) + (order.indexOf(fromKey) < order.indexOf(toKey) ? 1 : 0), 0, fromKey);
			save({ order: next });
		};

		return {
			columns: visibleColumns,
			picker: {
				items: orderedMovable.map((column) => ({
					key: column.key,
					label: layoutLabelOf(column),
					visible: isVisible(column),
					onEdit: column.onLayoutEdit,
				})),
				isCustomized: Boolean(saved),
				toggle,
				move,
				reset: () => setTableLayout(layoutKey, null),
			},
		};
	}, [layoutKey, columns, firstColumnSticky, saved, setTableLayout]);
}
